import {
  Section, Row, TextField, TextAreaField, SelectField,
  ToggleField, ChipsField, NumField, PairRows,
} from "./fields";

export function IdentitySection({ data, patch, onSave, saving, pii }) {
  return (
    <Section id="identity" title="Identity" subtitle="Legal name as it appears on tickets, plus contact details." onSave={onSave} saving={saving} testId="section-identity">
      <Row cols={3}>
        <TextField label="Legal first name" value={data.legal_first_name} onChange={(v) => patch({ legal_first_name: v })} placeholder="Adam" testId="id-first-name" />
        <TextField label="Middle name" value={data.legal_middle_name} onChange={(v) => patch({ legal_middle_name: v })} placeholder="Conrad" testId="id-middle-name" />
        <TextField label="Legal last name" value={data.legal_last_name} onChange={(v) => patch({ legal_last_name: v })} placeholder="Ahern" testId="id-last-name" />
      </Row>
      <Row cols={3}>
        <div>
          <label className="label-tag block mb-2">Date of birth</label>
          <input type="date" className="w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-ember"
            value={data.date_of_birth ?? ""} onChange={(e) => patch({ date_of_birth: e.target.value })} data-testid="id-dob" />
          {pii?.on_file?.includes("identity.date_of_birth") && !data.date_of_birth && (
            <p className="text-[11px] text-sand-500 mt-1" data-testid="id-dob-on-file">On file — hidden. Enter a new date to replace.</p>
          )}
        </div>
        <SelectField label="Gender (as on ID)" value={data.gender} onChange={(v) => patch({ gender: v })}
          options={[{ value: "", label: "—" }, { value: "male", label: "Male" }, { value: "female", label: "Female" }, { value: "unspecified", label: "Unspecified / X" }]}
          testId="id-gender" />
        <TextField label="Phone" value={data.phone} onChange={(v) => patch({ phone: v })} placeholder="+1 …" mono testId="id-phone" />
      </Row>
      <PairRows label="Emergency contacts" rows={data.emergency_contacts} onChange={(v) => patch({ emergency_contacts: v })}
        fields={[
          { key: "name", placeholder: "Name" },
          { key: "phone", placeholder: "Phone", width: "w-44" },
          { key: "relationship", placeholder: "Relationship", width: "w-36" },
        ]}
        addLabel="Add contact" testId="id-emergency" />
      <TextAreaField label="Notes" value={data.notes} onChange={(v) => patch({ notes: v })} placeholder="Name variations across bookings…" testId="id-notes" />
    </Section>
  );
}

export function DocumentsSection({ data, patch, onSave, saving }) {
  return (
    <Section id="documents" title="Travel documents" subtitle="Trusted traveler numbers and passports for bookings and check-in." onSave={onSave} saving={saving} testId="section-documents">
      <Row cols={3}>
        <TextField label="Known Traveler # (TSA PreCheck / Global Entry)" value={data.known_traveler_number} onChange={(v) => patch({ known_traveler_number: v })} placeholder="e.g. 98XXXXXXX" mono testId="doc-ktn" />
        <div>
          <label className="label-tag block mb-2">Global Entry expiry</label>
          <input type="date" className="w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-ember"
            value={data.global_entry_expiry ?? ""} onChange={(e) => patch({ global_entry_expiry: e.target.value })} data-testid="doc-ge-expiry" />
        </div>
        <TextField label="Redress number" value={data.redress_number} onChange={(v) => patch({ redress_number: v })} mono testId="doc-redress" />
      </Row>
      <PairRows label="Passports" rows={data.passports} onChange={(v) => patch({ passports: v })}
        fields={[
          { key: "country", placeholder: "Country", width: "w-28" },
          { key: "number", placeholder: "Passport number" },
          { key: "expiry", placeholder: "Expiry YYYY-MM-DD", width: "w-44" },
          { key: "full_name", placeholder: "Name on passport" },
        ]}
        addLabel="Add passport" testId="doc-passports" />
      <Row cols={3}>
        <TextField label="Driver's license number" value={data.drivers_license_number ?? ""} onChange={(v) => patch({ drivers_license_number: v })} mono testId="doc-dl-number" />
        <TextField label="Driver's license state" value={data.drivers_license_state} onChange={(v) => patch({ drivers_license_state: v })} placeholder="e.g. PR / CO" testId="doc-dl-state" />
        <div>
          <label className="label-tag block mb-2">Driver's license expiry</label>
          <input type="date" className="w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-ember"
            value={data.drivers_license_expiry ?? ""} onChange={(e) => patch({ drivers_license_expiry: e.target.value })} data-testid="doc-dl-expiry" />
        </div>
      </Row>
      <TextAreaField label="Notes" value={data.notes} onChange={(v) => patch({ notes: v })} placeholder="Visas, second citizenship…" testId="doc-notes" />
    </Section>
  );
}

const EMPTY_COMPANION = {
  name: "", relationship: "", date_of_birth: "", known_traveler_number: "",
  redress_number: "", passport_number: "", passport_country: "", passport_expiry: "",
  loyalty_numbers: [], notes: "",
};

const dateInputCls = "w-full border border-sand-300 bg-surface px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-ember";

function CompanionCard({ c, idx, onChange, onRemove, pii }) {
  const set = (patch) => onChange({ ...c, ...patch });
  return (
    <div className="border border-sand-200 p-4 space-y-4" data-testid={`companion-card-${idx}`}>
      <div className="flex items-center justify-between">
        <p className="label-tag">Companion {idx + 1}{c.name ? ` · ${c.name}` : ""}</p>
        <button type="button" onClick={onRemove} className="font-mono text-[10px] uppercase tracking-wide text-sand-400 hover:text-error" data-testid={`companion-remove-${idx}`}>
          Remove
        </button>
      </div>
      <Row cols={3}>
        <TextField label="Name" value={c.name} onChange={(v) => set({ name: v })} placeholder="Full legal name" testId={`cp-name-${idx}`} />
        <TextField label="Relationship" value={c.relationship} onChange={(v) => set({ relationship: v })} placeholder="Spouse, kid…" testId={`cp-relationship-${idx}`} />
        <div>
          <label className="label-tag block mb-2">Date of birth</label>
          <input type="date" className={dateInputCls} value={c.date_of_birth ?? ""} onChange={(e) => set({ date_of_birth: e.target.value })} data-testid={`cp-dob-${idx}`} />
          {pii?.on_file?.includes(`companions.companions.${idx}.date_of_birth`) && !c.date_of_birth && (
            <p className="text-[11px] text-sand-500 mt-1">On file — hidden. Enter a new date to replace.</p>
          )}
        </div>
      </Row>
      <Row cols={2}>
        <TextField label="Known Traveler # (TSA / Global Entry)" value={c.known_traveler_number} onChange={(v) => set({ known_traveler_number: v })} mono testId={`cp-ktn-${idx}`} />
        <TextField label="Redress number" value={c.redress_number} onChange={(v) => set({ redress_number: v })} mono testId={`cp-redress-${idx}`} />
      </Row>
      <Row cols={3}>
        <TextField label="Passport number" value={c.passport_number} onChange={(v) => set({ passport_number: v })} mono testId={`cp-passport-number-${idx}`} />
        <TextField label="Passport country" value={c.passport_country} onChange={(v) => set({ passport_country: v })} placeholder="USA" testId={`cp-passport-country-${idx}`} />
        <div>
          <label className="label-tag block mb-2">Passport expiry</label>
          <input type="date" className={dateInputCls} value={c.passport_expiry ?? ""} onChange={(e) => set({ passport_expiry: e.target.value })} data-testid={`cp-passport-expiry-${idx}`} />
        </div>
      </Row>
      <PairRows label="Frequent traveler numbers" rows={c.loyalty_numbers} onChange={(v) => set({ loyalty_numbers: v })}
        fields={[{ key: "program", placeholder: "Program (e.g. AAdvantage)" }, { key: "member_number", placeholder: "Member #", width: "w-44" }]}
        addLabel="Add program" testId={`cp-loyalty-${idx}`} />
      <TextField label="Notes" value={c.notes} onChange={(v) => set({ notes: v })} placeholder="Seat pairing, booking quirks…" testId={`cp-notes-${idx}`} />
    </div>
  );
}

export function CompanionsSection({ data, patch, onSave, saving, pii }) {
  const companions = data.companions || [];
  return (
    <Section id="companions" title="Companions & recurring trips" subtitle="Frequent co-travelers with their IDs and traveler numbers, plus the rhythms the AI should know about." onSave={onSave} saving={saving} testId="section-companions">
      <div className="space-y-4">
        {companions.map((c, i) => (
          <CompanionCard key={i} c={c} idx={i} pii={pii}
            onChange={(nc) => patch({ companions: companions.map((x, xi) => (xi === i ? nc : x)) })}
            onRemove={() => patch({ companions: companions.filter((_, xi) => xi !== i) })} />
        ))}
      </div>
      <button type="button" className="btn-swiss btn-outline text-xs"
        onClick={() => patch({ companions: [...companions, { ...EMPTY_COMPANION }] })}
        data-testid="cp-add-companion">
        + Add companion
      </button>
      <PairRows label="Recurring trips" rows={data.recurring_trips} onChange={(v) => patch({ recurring_trips: v })}
        fields={[
          { key: "name", placeholder: "Name (e.g. Colorado rotation)" },
          { key: "route", placeholder: "Route (e.g. SJU ⇄ DRO)", width: "w-48" },
          { key: "cadence", placeholder: "Cadence (e.g. every 6 weeks)", width: "w-48" },
        ]}
        addLabel="Add recurring trip" testId="cp-recurring" />
      <TextAreaField label="Notes" value={data.notes} onChange={(v) => patch({ notes: v })} placeholder="Seat pairing rules, who books what…" testId="cp-notes" />
    </Section>
  );
}

export function HealthSection({ data, patch, onSave, saving }) {
  return (
    <Section id="health" title="Health, fitness & diving" subtitle="Dive rules feed the planner as hard constraints; the rest is context." onSave={onSave} saving={saving} testId="section-health">
      <ToggleField label="Certified diver" hint="Enables the no-fly-after-diving rule below" checked={data.dive_certified} onChange={(v) => patch({ dive_certified: v })} testId="hl-dive-certified" />
      <Row cols={3}>
        <TextField label="Certification level" value={data.dive_cert_level} onChange={(v) => patch({ dive_cert_level: v })} placeholder="e.g. PADI AOW" testId="hl-dive-level" />
        <TextField label="Cert number" value={data.dive_cert_number} onChange={(v) => patch({ dive_cert_number: v })} mono testId="hl-dive-number" />
        <TextField label="DAN insurance #" value={data.dan_insurance_number} onChange={(v) => patch({ dan_insurance_number: v })} mono testId="hl-dan" />
      </Row>
      <Row cols={2}>
        <NumField label="No-fly window after diving (hours)" value={data.no_fly_hours_after_diving} onChange={(v) => patch({ no_fly_hours_after_diving: v ?? 18 })} min={0} max={48} testId="hl-no-fly-hours" />
        <TextField label="Fitness needs on the road" value={data.fitness_needs} onChange={(v) => patch({ fitness_needs: v })} placeholder="Running routes, lap pool, gym…" testId="hl-fitness" />
      </Row>
      <Row cols={2}>
        <ChipsField label="Allergies" values={data.allergies} onChange={(v) => patch({ allergies: v })} placeholder="e.g. shellfish" testId="hl-allergies" />
        <ChipsField label="Dietary preferences" values={data.dietary_preferences} onChange={(v) => patch({ dietary_preferences: v })} placeholder="e.g. high protein" testId="hl-dietary" />
      </Row>
      <TextAreaField label="Medical notes" value={data.medical_notes} onChange={(v) => patch({ medical_notes: v })} placeholder="Medications to pack, conditions relevant to travel…" testId="hl-medical" />
    </Section>
  );
}

export function EntitiesSection({ data, patch, onSave, saving }) {
  return (
    <Section id="entities" title="Entities & attribution" subtitle="Assign trips to a business or personal entity — the Budget page can filter by these." onSave={onSave} saving={saving} testId="section-entities">
      <PairRows label="Entities" rows={data.entities} onChange={(v) => patch({ entities: v })}
        fields={[
          { key: "name", placeholder: "Entity name" },
          { key: "kind", placeholder: "business / personal", width: "w-40" },
          { key: "gl_code", placeholder: "GL code", width: "w-28" },
          { key: "billable_default", placeholder: "Billable", type: "toggle" },
        ]}
        addLabel="Add entity" testId="en-entities" />
      <TextField label="Default entity for new trips" value={data.default_entity} onChange={(v) => patch({ default_entity: v })} placeholder="e.g. Personal" testId="en-default" />
      <TextAreaField label="Notes" value={data.notes} onChange={(v) => patch({ notes: v })} placeholder="Billing rules, which client codes map where…" testId="en-notes" />
    </Section>
  );
}
